import { useEffect, useMemo } from 'react'
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import {
  CellTower,
  Cloud,
  DeviceMobile,
  Fingerprint,
  GlobeHemisphereWest,
  IdentificationBadge,
  LockKey,
  ShieldCheck,
  ShieldWarning,
  UserCircle,
  Wrench,
} from '@phosphor-icons/react'
import { buildImeiHubPreviewSections, type ImeiHubPreviewServiceId } from '@redevicer/shared'
import { useTranslation } from 'react-i18next'
import '../styles/imei-checkout.css'

const CHECK_ICON: Record<string, typeof ShieldCheck> = {
  'vc.imeiHub.services.lock.check1': Cloud,
  'vc.imeiHub.services.lock.check2': LockKey,
  'vc.imeiHub.services.lock.check3': UserCircle,
  'vc.imeiHub.services.lock.check4': ShieldWarning,
  'vc.imeiHub.services.passport.check1': DeviceMobile,
  'vc.imeiHub.services.passport.check2': IdentificationBadge,
  'vc.imeiHub.services.passport.check3': ShieldCheck,
  'vc.imeiHub.services.passport.check4': CellTower,
  'vc.imeiHub.services.safecheck.check1': ShieldWarning,
  'vc.imeiHub.services.safecheck.check2': ShieldCheck,
  'vc.imeiHub.services.safecheck.check3': GlobeHemisphereWest,
  'vc.imeiHub.services.business.check1': IdentificationBadge,
  'vc.imeiHub.services.business.check2': UserCircle,
  'vc.imeiHub.services.business.check3': ShieldCheck,
  'vc.imeiHub.services.business.check4': ShieldWarning,
  'vc.imeiHub.services.gsx.check1': Wrench,
  'vc.imeiHub.services.gsx.check2': IdentificationBadge,
  'vc.imeiHub.services.gsx.check3': LockKey,
  'vc.imeiHub.services.gsx.check4': DeviceMobile,
  'vc.imeiHub.services.tmobile.check1': CellTower,
  'vc.imeiHub.services.tmobile.check2': ShieldWarning,
  'vc.imeiHub.services.tmobile.check3': Fingerprint,
}

const HUB_IDS: readonly ImeiHubPreviewServiceId[] = [
  'verify',
  'passport',
  'lock',
  'carrier',
  'safecheck',
  'business',
  'android',
  'gsx',
  'tmobile',
]

const SHORT_NAME: Record<string, string> = {
  lock: 'Lock',
  safecheck: 'Safe Check',
  business: 'Business',
  gsx: 'GSX',
  tmobile: 'T-Mobile',
  passport: 'Passport',
}

function formatEur(cents: number, lithuanian: boolean): string {
  const raw = (Math.max(0, cents) / 100).toFixed(2)
  return `€${lithuanian ? raw.replace('.', ',') : raw}`
}

function CheckoutArt() {
  return (
    <img
      className="ichk-art"
      src="/images/imei-checkout-lock.jpg"
      alt=""
    />
  )
}

export default function ImeiCheckoutPage() {
  const { t, i18n } = useTranslation()
  const lithuanian = (i18n.language || '').toLowerCase().startsWith('lt')
  const params = useMemo(() => new URLSearchParams(window.location.search), [])
  const isReturn = /\/imei-checkout\/return\/?$/.test(window.location.pathname)

  useEffect(() => {
    if (!isReturn) return
    const sessionId = params.get('session_id') || ''
    const service = params.get('service') || ''
    const client = params.get('client') || 'web'
    const query = `imei_check=success&service=${encodeURIComponent(service)}&session_id=${encodeURIComponent(sessionId)}`
    if (client === 'mobile') {
      window.location.replace(`redevicer://imei-check?${query}`)
      return
    }
    window.location.replace(`/?${query}`)
  }, [isReturn, params])

  const secret = params.get('cs') || ''
  const publishableKey = params.get('pk') || ''
  const service = (params.get('service') || 'lock').toLowerCase()
  const cents = Number(params.get('cents') || '0')
  const short = SHORT_NAME[service] || 'Check'
  const product = `reDevicer · ${short}`
  const hubId: ImeiHubPreviewServiceId = (HUB_IDS as readonly string[]).includes(service)
    ? (service as ImeiHubPreviewServiceId)
    : 'lock'
  const features = buildImeiHubPreviewSections(hubId).map((row) => {
    const noteKey = `${row.titleKey}Note`
    const note = t(noteKey)
    return {
      key: row.titleKey,
      title: t(row.titleKey),
      body: note === noteKey ? t(`vc.imeiHub.services.${hubId}.detail`) : note,
      Icon: CHECK_ICON[row.titleKey] ?? ShieldCheck,
    }
  })
  const stripePromise = useMemo(
    () => (publishableKey.startsWith('pk_') ? loadStripe(publishableKey) : null),
    [publishableKey],
  )

  const copy = lithuanian
    ? {
        badge: 'Saugi ir patikima paslauga',
        subtitle: t(`vc.imeiHub.services.${hubId}.desc`),
        lead: t(`vc.imeiHub.services.${hubId}.detail`),
        includes: 'Šioje ataskaitoje',
        how: 'Kaip tai veikia?',
        steps: [
          ['Atlikite mokėjimą', 'Saugiai atsiskaitykite per Stripe.'],
          ['Gaukite prieigą', 'Paslauga aktyvuojama iškart.'],
          ['Mėgaukitės', `Naudokite „${product}“ savo įrenginyje.`],
        ],
        back: 'Grįžti',
        missing: 'Nepavyko atidaryti mokėjimo. Grįžkite ir bandykite dar kartą.',
        returning: 'Grąžiname po apmokėjimo…',
      }
    : {
        badge: 'Secure and trusted',
        subtitle: t(`vc.imeiHub.services.${hubId}.desc`),
        lead: t(`vc.imeiHub.services.${hubId}.detail`),
        includes: 'In this report',
        how: 'How it works',
        steps: [
          ['Pay', 'Check out securely with Stripe.'],
          ['Get access', 'The service unlocks immediately.'],
          ['Use it', `Run “${product}” on your device.`],
        ],
        back: 'Back',
        missing: 'Checkout could not be opened. Go back and try again.',
        returning: 'Returning after payment…',
      }

  if (isReturn) {
    return (
      <div className="ichk-boot">
        <p>{copy.returning}</p>
      </div>
    )
  }

  return (
    <div className="ichk">
      <section className="ichk-hero">
        <div className="ichk-hero__top">
          <div className="ichk-brand">
            <img src="/logo-mark.png?v=4" alt="" />
            <span>reDevicer</span>
          </div>
          <button type="button" className="ichk-back" onClick={() => window.history.back()}>
            {copy.back}
          </button>
        </div>
        <CheckoutArt />
        <div className="ichk-copy">
          <p className="ichk-badge">{copy.badge}</p>
          <h1>{product}</h1>
          <p className="ichk-sub">{copy.subtitle}</p>
          {cents > 0 ? <p className="ichk-price">{formatEur(cents, lithuanian)}</p> : null}
          <p className="ichk-lead">{copy.lead}</p>
        </div>
        <div className="ichk-includes">
          <h2>{copy.includes}</h2>
          <div className="ichk-features">
            {features.map(({ key, title, body, Icon }) => (
              <article key={key} className="ichk-feature">
                <span className="ichk-feature__icon">
                  <Icon size={22} weight="fill" />
                </span>
                <div>
                  <h2>{title}</h2>
                  <p>{body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
        <div className="ichk-how">
          <h2>{copy.how}</h2>
          <ol>
            {copy.steps.map(([title, body], index) => (
              <li key={title}>
                <span>{index + 1}</span>
                <div>
                  <strong>{title}</strong>
                  <p>{body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>
      <section className="ichk-pay">
        {stripePromise && secret ? (
          <div className="ichk-pay__frame">
            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret: secret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        ) : (
          <p className="ichk-missing">{copy.missing}</p>
        )}
      </section>
    </div>
  )
}
